import PlanoProDetalhes from '../pages/usuario/PlanoProDetalhes';
import React from 'react';
import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import pacienteService from '../services/pacienteService';

// Páginas públicas
import Login from '../pages/Login';
import Cadastro from '../pages/Cadastro';
import RecuperarSenha from '../pages/RecuperarSenha';
import ResetPassword from '../pages/ResetPassword';

// Páginas de usuário
import Dashboard from '../pages/usuario/Dashboard';
import Faturamento from '../pages/usuario/Faturamento';
import GerenciarMaquinasCartao from '../pages/usuario/GerenciarMaquinasCartao';
import Despesas from '../pages/usuario/Despesas';
import PlanoContas from '../pages/usuario/PlanoContas';
import Lancamentos from '../pages/usuario/Lancamentos';
import Relatorios from '../pages/usuario/Relatorios';
import Analise from '../pages/usuario/Analise';
import Pacientes from '../pages/usuario/Pacientes';
import FichaPaciente from '../pages/usuario/FichaPaciente';
import EmitirDocumento from '../pages/usuario/EmitirDocumento';
import Procedimentos from '../pages/usuario/Procedimentos';
import Agenda from '../pages/usuario/Agenda';
import PerfilDentistaPage from '../pages/PerfilDentistaPage';
import ClinicaDashboard from '../pages/usuario/ClinicaDashboard';

import Assinatura from '../pages/usuario/Assinatura';
import PagamentoPro from '../pages/usuario/PagamentoPro';
import Configuracao from '../pages/usuario/Configuracao';

// Páginas de admin
import DashboardAdmin from '../pages/admin/DashboardAdmin';
import Operacional from '../pages/admin/Operacional';
import VisionTax from '../pages/admin/VisionTax';
import PerfilUsuario from '../pages/admin/PerfilUsuario';
import PerfilClinica from '../pages/admin/PerfilClinica';
import UsuariosAdmin from '../pages/admin/UsuariosAdmin';
import EstatisticasAdmin from '../pages/admin/EstatisticasAdmin';
import ConfiguracoesAdmin from '../pages/admin/ConfiguracoesAdmin';
import RelatoriosAdmin from '../pages/admin/RelatoriosAdmin';
import RoboAutomacao from '../pages/admin/RoboAutomacao';
import SugestoesAdmin from '../pages/admin/SugestoesAdmin';
import Sugestoes from '../pages/usuario/Sugestoes';
import Termos from '../pages/usuario/Termos';
import AssinarDocumento from '../pages/public/AssinarDocumento';
import GerenciarUsuarios from '../pages/usuario/GerenciarUsuarios';
import Layout from '../components/Layout';
import LayoutAdmin from '../components/LayoutAdmin';

// Wrapper para passar paciente e tipo para EmitirDocumento
const EmitirDocumentoWrapper = () => {
  const { id, tipo } = useParams();
  const [paciente, setPaciente] = useState(null);
  useEffect(() => {
    pacienteService.buscarPorId(id).then(setPaciente);
  }, [id]);
  if (!paciente) return <div>Carregando...</div>;
  return <EmitirDocumento paciente={paciente} tipo={tipo} />;
};

// Assinatura com cobrança vencida: trava o usuário na tela de pagamento até regularizar
const ROTAS_LIBERADAS_INADIMPLENTE = ['/usuario/plano-pro', '/usuario/assinatura', '/usuario/pagamentopro'];

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  if (loading) return <div>Carregando...</div>;
  if (!isAuthenticated) return <Navigate to="/login" />;
  const inadimplente = user?.clinica?.inadimplente;
  if (inadimplente && !user?.isAdmin && !ROTAS_LIBERADAS_INADIMPLENTE.includes(location.pathname)) {
    return <Navigate to="/usuario/plano-pro" />;
  }
  return children;
};

// Rota que verifica permissão específica por módulo
const PermissaoRoute = ({ children, modulo }) => {
  const { isAuthenticated, user, loading } = useAuth();
  if (loading) return <div>Carregando...</div>;
  if (!isAuthenticated) return <Navigate to="/login" />;
  // Só o dentista dono da clínica tem acesso total automático; secretaria E
  // dentista parceiro são sempre limitados pelas permissoes configuradas.
  if (user?.role !== 'dentista' && modulo && !user?.permissoes?.[modulo]) {
    return <Navigate to="/agenda" />;
  }
  return children;
};

// Gerenciar Usuários mexe em contas/permissões de outros usuários — só o
// dentista dono da clínica pode acessar, nunca secretaria nem dentista parceiro.
const DonoClinicaRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();
  if (loading) return <div>Carregando...</div>;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.role !== 'dentista') return <Navigate to="/agenda" />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();
  if (loading) return <div>Carregando...</div>;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!user?.isAdmin) return <Navigate to="/dashboard" />;
  return children;
};


const AppRoutes = () => {
  const { isAuthenticated, user } = useAuth();
  const getInitialRoute = () => {
    if (!isAuthenticated) return '/login';
    if (user?.isAdmin) return '/admin';
    if (user?.role === 'secretaria') {
      const perm = user?.permissoes || {};
      if (perm.agenda) return '/agenda';
      if (perm.pacientes) return '/pacientes';
      return '/perfil';
    }
    return '/dashboard';
  };

  return (
    <Routes>
      {/* Rotas públicas */}
      <Route path="/assinar/:token" element={<AssinarDocumento />} />
      <Route path="/login" element={<Login />} />
      <Route path="/cadastro" element={<Cadastro />} />
      <Route path="/recuperar-senha" element={<RecuperarSenha />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Rotas privadas */}
      <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route index element={<Navigate to={getInitialRoute()} />} />
        <Route path="dashboard" element={<PermissaoRoute modulo="dashboard"><Dashboard /></PermissaoRoute>} />
        <Route path="faturamento" element={<PermissaoRoute modulo="faturamento"><Faturamento /></PermissaoRoute>} />
        <Route path="maquinas-cartao" element={<PermissaoRoute modulo="faturamento"><GerenciarMaquinasCartao /></PermissaoRoute>} />
        <Route path="despesas" element={<PermissaoRoute modulo="despesas"><Despesas /></PermissaoRoute>} />
        <Route path="plano-contas" element={<PermissaoRoute modulo="planoContas"><PlanoContas /></PermissaoRoute>} />
        <Route path="lancamentos" element={<PermissaoRoute modulo="lancamentos"><Lancamentos /></PermissaoRoute>} />
        <Route path="relatorios" element={<PermissaoRoute modulo="relatorios"><Relatorios /></PermissaoRoute>} />
        <Route path="analise" element={<PermissaoRoute modulo="analise"><Analise /></PermissaoRoute>} />
        <Route path="agenda" element={<PermissaoRoute modulo="agenda"><Agenda /></PermissaoRoute>} />
        <Route path="pacientes" element={<PermissaoRoute modulo="pacientes"><Pacientes /></PermissaoRoute>} />
        <Route path="pacientes/:id" element={<PermissaoRoute modulo="pacientes"><FichaPaciente /></PermissaoRoute>} />
        <Route path="pacientes/:id/documento/:tipo" element={<PermissaoRoute modulo="pacientes"><EmitirDocumentoWrapper /></PermissaoRoute>} />
        <Route path="procedimentos" element={<PermissaoRoute modulo="procedimentos"><Procedimentos /></PermissaoRoute>} />
        <Route path="clinica-dashboard" element={<PermissaoRoute modulo="clinicaDashboard"><ClinicaDashboard /></PermissaoRoute>} />
        <Route path="perfil" element={<PerfilDentistaPage />} />
        <Route path="gerenciar-usuarios" element={<DonoClinicaRoute><GerenciarUsuarios /></DonoClinicaRoute>} />
  <Route path="usuario/assinatura" element={<Assinatura />} />
  <Route path="usuario/pagamentopro" element={<PagamentoPro />} />
  <Route path="usuario/plano-pro" element={<PlanoProDetalhes />} />
  <Route path="usuario/configuracao" element={<Configuracao />} />
  <Route path="sugestoes" element={<Sugestoes />} />
  <Route path="termos" element={<Termos />} />
      </Route>

      {/* Rotas administrativas */}
      <Route path="/admin" element={<AdminRoute><LayoutAdmin /></AdminRoute>}>
        <Route index element={<DashboardAdmin />} />
        <Route path="operacional" element={<Operacional />} />
        <Route path="vision-tax" element={<VisionTax />} />
        <Route path="operacional/usuario/:userId" element={<PerfilUsuario />} />
        <Route path="operacional/clinica/:clinicaId" element={<PerfilClinica />} />
        <Route path="usuarios" element={<UsuariosAdmin />} />
        <Route path="relatorios" element={<RelatoriosAdmin />} />
        <Route path="automacao" element={<RoboAutomacao />} />
        <Route path="sugestoes" element={<SugestoesAdmin />} />
      </Route>

      {/* Rota 404 */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

export default AppRoutes;
